import { DateTime } from 'luxon';
import { CouchLoginResult } from '@cf-shared/couch-auth';
import { formatDateMedPadded } from '@cf-shared/date-format';
import { resolveSubscriptionEndInstants } from '@cf-shared/data-model/subscription-end-instants';
import {
  CouchUserPaddleScheduledChange,
  SubscriptionStatus,
  UserLicenseType,
  isPausedPaddleSubscriber,
} from '@cf-shared/data-model/couch-user';
import {
  OffPlatformNoteInfo,
  SubscriptionBillingProvider,
  describeOffPlatformNote,
  isInAppCancelAvailable,
  isInAppResumeAvailable,
  isMembershipManageableOnPlatform,
  shouldShowWebMembershipProblemNote,
} from '@cf-shared/data-model/subscription-channel-rules';
import { TranslatedString } from 'models/translated-string';
import { shouldShowResumeButton, shouldSuppressUpgradeBlock } from './paddle-paused-ui-policy';
import { shouldShowCoachCoversMembershipNotice } from './coach-covers-membership-policy';

/**
 * Everything the User Profile subscription card DECIDES, as one pure function.
 *
 * These rules used to be 18 getters on `UserProfilePage`, read whenever the page
 * re-rendered. They live here now so every rule can be unit-tested with plain
 * values (`subscription-card-model.test.ts`), and so the page recomputes the card
 * in exactly one place (`UserProfilePage.renderCard`). Deferred finding
 * `df-41c084aa52fc` has the history.
 *
 * Nothing here imports Angular or a service. Every value a rule needs arrives in
 * `SubscriptionCardInputs`, which is what makes the rules testable at all — the
 * repository has no component test harness.
 *
 * ## The one rule most of these comments are about
 *
 * `provider` is the SERVER's answer, from `getSubscriptionInfo`, and it is only
 * ever assigned after a successful call. On a transient failure it stays null.
 * Several rules below therefore read the LOCAL login result (`couch`) instead,
 * because a null provider on that failure path would otherwise tell a subscriber
 * something false about their own membership. Each rule that does this says why.
 */

/** Which label the "Subscription type" row shows. The card maps these to
 *  translated text; they are kept out of this module so it stays pure. */
export enum SubscriptionTypeLabelKind {
  /** no current plan is known */
  None = 'None',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
  /** a plan length the app has no word for — show the plan's own title */
  PlanTitle = 'PlanTitle',
}

/** Which "current plan" note the regular-active block shows. */
export enum PlanNoteMode {
  Hidden = 'Hidden',
  /** a downgrade or upgrade is queued for the next renewal */
  Pending = 'Pending',
  /** the other plan is cheaper per month — quote the saving */
  Saving = 'Saving',
  Plain = 'Plain',
}

/**
 * The parts of a billing plan the card reads.
 *
 * Declared here rather than imported, because the real `PlanOption` lives in a
 * service file and importing it would pull Angular into a module that must not
 * have it. `PlanOption` fits this shape, so it is passed straight in.
 */
export interface SubscriptionCardPlan {
  title: string;
  priceText: string;
  saving?: string;
  numMonths: number;
}

/** Every value the card's rules read. The page assembles it; see `renderCard`. */
export interface SubscriptionCardInputs {
  // ── the page's own state, written by populateUserDetails / synchronizeSlowData ──

  /** the page's role bucket, an `AuthRoleDesc.id` — 'paid' only while access is live */
  roleClass: string;
  /** the role's translated display name, e.g. "Premium" */
  roleName: TranslatedString;
  /** the role's translated one-line explanation, always shown on the card */
  roleInfo: TranslatedString;
  roleIsTemporary: boolean;
  /**
   * The page's copy: seeded from the login result, then overwritten with the
   * server's answer once the refresh lands. Drives the wording blocks.
   */
  canCancelSubscription: boolean;
  /** the formatted trial-end date, or '' when not in an active trial */
  trialEnds: string;
  /** the formatted access-end date, or '' */
  licenseExpiry: string;
  /**
   * The RAW stored expiry that `licenseExpiry` was formatted from.
   *
   * Both of the card's dates are derived from this one value, so they can never
   * describe different subscriptions. Reading the provider date from
   * `couch.licenseExpires_utc` instead would drift in the degraded login path,
   * where `getAuthRoleAndExpiry` answers from the CACHED expiry while `couch` is
   * present but carries no `roles` (see auth.service.ts).
   */
  licenseExpiryIso: string | undefined;
  loadingSlowData: boolean;
  subscriptionInfoError: boolean;
  hasPayProData: boolean;
  hasPaddleData: boolean;
  /** false once the member dismissed the switch-plan banner this visit */
  showSwitchPlanBanner: boolean;

  // ── the environment ──

  platform: 'android' | 'ios' | 'web';
  now: DateTime;
  /** the locale the card's dates are formatted in (the page passes BOOT_LOCALE) */
  locale: string;

  // ── the subscription service (server-derived) ──

  provider: SubscriptionBillingProvider | null;
  canSwitchPlan: boolean;
  currentPlan: SubscriptionCardPlan | null;
  otherPlan: SubscriptionCardPlan | null;
  pendingPlanChange: { toTitle: string } | null;
  isPaymentFailing: boolean;
  canManageSubscription: boolean;
  /**
   * The server's cancel flag, read straight off the service. Kept separate from
   * `canCancelSubscription` because the Cancel ROW has always read the service's
   * value directly, while the wording blocks read the page's copy.
   */
  serverCanCancelSubscription: boolean;
  /**
   * The server's resume flag, read straight off the service. Only the
   * pause-SCHEDULED arm of the Resume button consults it: that state is the one
   * the server can refuse for a reason no local field carries (a dunning
   * subscription, where resuming cannot make a failed charge succeed). The
   * already-paused arm deliberately does NOT read it — a failed refresh leaves
   * it false, and a paused subscriber must keep their way back. See
   * `shouldShowResumeButton`.
   */
  serverCanResumeSubscription: boolean;
  subscriptionStatus: SubscriptionStatus | null;

  // ── the local login result ──

  couch: CouchLoginResult | null | undefined;
}

/**
 * What the card shows. Plain values only, so a test can compare the whole thing.
 *
 * It holds two kinds of field. The DECISIONS are what the rules below compute.
 * The DISPLAY values are copied straight from the inputs, unchanged — they are
 * here so the card component can take this one object as its only data input,
 * rather than a decision object plus a dozen loose values that could drift out
 * of step with it.
 */
export interface SubscriptionCardModel {
  // ── display values, copied from the inputs unchanged ──

  roleClass: string;
  roleName: TranslatedString;
  roleInfo: TranslatedString;
  roleIsTemporary: boolean;
  canCancelSubscription: boolean;
  trialEnds: string;
  /**
   * The formatted date OUR ACCESS ends — what a member experiences as the last
   * day the app lets them in. Copied from the inputs unchanged.
   *
   * Captions the "Expires On" row and the "ends on" / "will end on" notes. For
   * the date the PROVIDER acts on, see {@link providerActsDate}; which sentence
   * wants which is settled in `resolveCardDates` below.
   */
  licenseExpiry: string;
  /**
   * The formatted date the PROVIDER renews, charges, pauses or cancels — a day
   * before {@link licenseExpiry} on the rails that store a graced expiry, and
   * the same day on Apple.
   *
   * Captions the "Renewal Date" / "Pauses On" row and the "pauses on" /
   * "scheduled to switch" notes. Falls back to {@link licenseExpiry} when the
   * provider's own date cannot be worked out, so a member never loses a date
   * they had before.
   */
  providerActsDate: string;

  // ── one field per SENTENCE on the card ──
  //
  // The template does no choosing: each sentence reads the field named after it.
  // Which of the two questions a sentence asks is decided once, in
  // `resolveCardDates` below, where a unit test can see it. Deciding that at the
  // display site is the mechanism behind all three #4189 regressions.

  /** the labelled date row — "Pauses On" / "Renewal Date" / "Expires On" */
  dateRowDate: string;
  /** "Your subscription pauses on {date}" */
  pauseNoteDate: string;
  /** "Your plan is scheduled to switch to \"{plan}\" on {date}." */
  planChangeNoteDate: string;
  /** "...if the payment keeps failing, your membership will end on {date}." */
  paymentFailingNoteDate: string;
  /** "Your subscription ends on {date}" */
  subscriptionEndsNoteDate: string;
  loadingSlowData: boolean;
  subscriptionInfoError: boolean;
  canSwitchPlan: boolean;
  isPaymentFailing: boolean;
  currentPlan: SubscriptionCardPlan | null;
  otherPlan: SubscriptionCardPlan | null;
  pendingPlanChange: { toTitle: string } | null;

  // ── decisions ──

  canShowSwitchPlanBanner: boolean;
  subscribedOffPlatform: boolean;
  canCancelHere: boolean;
  canResumeHere: boolean;
  hasPendingPaddlePause: boolean;
  hasPendingPaddleResume: boolean;
  /**
   * Whether the date row asks when the PROVIDER acts ("Pauses On" / "Renewal
   * Date") rather than when OUR access ends ("Expires On").
   *
   * ONE field drives both the label and the date under it. Keeping them on
   * separate conditions is how the card came to caption one date with another
   * date's word — see `resolveProviderActsDate` and #4189.
   */
  dateRowAsksProviderAction: boolean;
  isPaddlePaused: boolean;
  suppressUpgradeBlock: boolean;
  showResumeButton: boolean;
  canShowManageSubscription: boolean;
  showWebMembershipProblemNote: boolean;
  showOffPlatformNote: boolean;
  showCoachCoversMembershipNote: boolean;
  offPlatformNote: OffPlatformNoteInfo | null;
  planNoteMode: PlanNoteMode;
  /** true when the badge reads "<role> - <plan title>" rather than the role alone */
  badgeIncludesPlanTitle: boolean;
  subscriptionTypeLabelKind: SubscriptionTypeLabelKind;
  /**
   * The plan length in months when it is neither monthly nor yearly AND the card
   * shows the type label, otherwise null. A pure function cannot report anything,
   * so the page reports this once per recompute — see `buildCard`.
   */
  unknownRecurrenceMonths: number | null;
  /** whether the Cancel row below the support card is shown */
  showCancelRow: boolean;
}

/**
 * Both dates the card can show, and which one each SENTENCE gets.
 *
 * ## Why the choosing happens here and not in the template
 *
 * The card's date row picks its LABEL three ways — "Pauses On", "Renewal Date"
 * or "Expires On" — and the first two ask when the PROVIDER acts while the third
 * asks when OUR ACCESS ends. Until #4278 the label was chosen in this module
 * while the date beside it was computed on the page and routed to each sentence
 * in the template, with nothing tying the three together. That gap is the
 * mechanism behind all three of the #4189 regressions, where a fix to one date
 * field put two dates for one moment on screen at the next field.
 *
 * So every sentence's date is decided here, in one function a unit test can see,
 * and the template only reads the field named after the sentence. A future
 * sentence gets a field here; it never picks between the two at the display site.
 *
 * ## How a sentence is classified
 *
 * By what its English text promises, not by which field used to feed it:
 * "ends on", "will end on", "keep access until" are access-ends; "Renewal Date",
 * "Pauses On", "pauses on", "scheduled to switch ... on" are provider-acts. The
 * whole rule, and the per-rail grace table behind it, is in
 * design-docs/iap-subscriptions.md, "Access-ends versus provider-acts".
 *
 * ## The fall-backs, and why they are not failures
 *
 * The provider date falls back to the access date whenever it cannot be worked
 * out — an unknown rail, the offline path, or a coached client's mirrored grant,
 * which copies a coach's expiry without recording which store backs it. Showing
 * the access date there is a day out at worst; showing nothing would take away a
 * date the member has always had. An empty access date stays empty, so the two
 * can never disagree about whether there is a date at all, and 'Perpetual' (what
 * the page writes for an indefinite licence) carries straight through.
 */
function resolveCardDates(inputs: SubscriptionCardInputs, dateRowAsksProviderAction: boolean): {
  providerActsDate: string;
  dateRowDate: string;
  pauseNoteDate: string;
  planChangeNoteDate: string;
  paymentFailingNoteDate: string;
  subscriptionEndsNoteDate: string;
} {
  const accessEnds = inputs.licenseExpiry;
  const providerActs = resolveProviderActsDate(inputs);
  return {
    providerActsDate: providerActs,
    dateRowDate: dateRowAsksProviderAction ? providerActs : accessEnds,
    pauseNoteDate: providerActs,
    planChangeNoteDate: providerActs,
    paymentFailingNoteDate: accessEnds,
    subscriptionEndsNoteDate: accessEnds,
  };
}

/** The provider's own date as a person reads it, or the access date when it
 *  cannot be worked out. See {@link resolveCardDates} for the whole rule. */
function resolveProviderActsDate(inputs: SubscriptionCardInputs): string {
  if (!inputs.licenseExpiry) return '';
  // derived from the SAME stored value the page formatted `licenseExpiry` from,
  // so the two dates always describe the same subscription.
  const { providerActsAt } = resolveSubscriptionEndInstants(
    inputs.couch?.licenseType,
    inputs.licenseExpiryIso,
    inputs.couch ?? {},
  );
  if (!providerActsAt) return inputs.licenseExpiry;
  // localised the same way the page localises the access date: the day a person
  // acts on must be their OWN day, or somebody at UTC-7 whose subscription ends
  // 02:00Z is told the following date.
  return formatDateMedPadded(providerActsAt.toLocal(), inputs.locale);
}

/** Build the whole card from its inputs. */
export function buildSubscriptionCardModel(inputs: SubscriptionCardInputs): SubscriptionCardModel {
  const subscribedOffPlatform = isSubscribedOffPlatform(inputs);
  const canCancelHere = isInAppCancelAvailable(inputs.provider, inputs.platform);
  const canResumeHere = isResumeReachableHere(inputs);
  const pendingChange = pendingPaddleChange(inputs);
  const hasPendingPaddlePause = pendingChange?.action === 'pause';
  const isPaddlePaused = isPausedPaddleSubscriber(inputs.couch);
  const showWebMembershipProblemNote = isWebMembershipProblemNoteShown(inputs);
  const currentPlan = inputs.currentPlan;
  // the three-way label pick, as a single decision. A pending PAUSE has to be
  // asked about in its own right because it leaves canCancelSubscription TRUE
  // (#4247), so it cannot be folded into the cancel/renew pair.
  const dateRowAsksProviderAction = hasPendingPaddlePause
    || inputs.canCancelSubscription
    || pendingChange?.action === 'resume';
  const dates = resolveCardDates(inputs, dateRowAsksProviderAction);

  return {
    roleClass: inputs.roleClass,
    roleName: inputs.roleName,
    roleInfo: inputs.roleInfo,
    roleIsTemporary: inputs.roleIsTemporary,
    canCancelSubscription: inputs.canCancelSubscription,
    trialEnds: inputs.trialEnds,
    licenseExpiry: inputs.licenseExpiry,
    ...dates,
    loadingSlowData: inputs.loadingSlowData,
    subscriptionInfoError: inputs.subscriptionInfoError,
    canSwitchPlan: inputs.canSwitchPlan,
    isPaymentFailing: inputs.isPaymentFailing,
    currentPlan,
    otherPlan: inputs.otherPlan,
    pendingPlanChange: inputs.pendingPlanChange,

    canShowSwitchPlanBanner: canShowSwitchPlanBanner(inputs, subscribedOffPlatform),
    subscribedOffPlatform,
    canCancelHere,
    canResumeHere,
    hasPendingPaddlePause,
    hasPendingPaddleResume: pendingChange?.action === 'resume',
    dateRowAsksProviderAction,
    isPaddlePaused,
    // A paused subscriber has no access and so looks like a free user to
    // `roleClass`; inviting them to buy would double-charge them and orphan the
    // paused sub.
    suppressUpgradeBlock: shouldSuppressUpgradeBlock({ roleClass: inputs.roleClass, isPaddlePaused }),
    showResumeButton: isResumeButtonShown(inputs, {
      hasPendingPaddlePause,
      isPaddlePaused,
      canResumeHere,
      showWebMembershipProblemNote,
    }),
    canShowManageSubscription: isManageSubscriptionShown(inputs, subscribedOffPlatform),
    showWebMembershipProblemNote,
    showOffPlatformNote: isOffPlatformNoteShown(inputs, { subscribedOffPlatform, canCancelHere, canResumeHere }),
    showCoachCoversMembershipNote: isCoachCoversMembershipNoteShown(inputs),
    // Exhaustive over every provider so none can fall through to a wrong store
    // name — the #4277 fix (a Paddle sub used to read "managed via Apple", #4268).
    // Null when there is no provider, where the note does not apply.
    offPlatformNote: describeOffPlatformNote(inputs.provider),
    planNoteMode: resolvePlanNoteMode(inputs),
    // a paid member with a known plan reads "Premium - Monthly"; everyone else
    // reads the role name alone
    badgeIncludesPlanTitle: inputs.roleClass === 'paid' && !!currentPlan,
    subscriptionTypeLabelKind: resolveSubscriptionTypeLabelKind(currentPlan),
    unknownRecurrenceMonths: resolveUnknownRecurrenceMonths(inputs),
    // Shown when the server says the sub is cancellable AND an in-app cancel
    // mechanism is reachable here: web (PayPro) subs cancel via our server on any
    // platform — including the native app (#3826) — while store subs stay
    // cancellable only on their own platform.
    showCancelRow: inputs.serverCanCancelSubscription && canCancelHere,
  };
}

/**
 * Show the switch-plan banner only when the member can upgrade to a more
 * expensive plan (monthly to yearly).
 */
function canShowSwitchPlanBanner(inputs: SubscriptionCardInputs, subscribedOffPlatform: boolean): boolean {
  return inputs.showSwitchPlanBanner &&
    inputs.canSwitchPlan &&
    !subscribedOffPlatform &&
    inputs.currentPlan?.numMonths === 1 &&
    // the banner copy quotes the other plan's price and saving; Paddle plans
    // carry no priceText (see PaddleSubscriptionStrategy.loadPlans), so hide it there rather than
    // render "upgrade for {blank}".
    !!inputs.otherPlan?.priceText;
}

/**
 * True when the membership lives on a provider that cannot be managed from THIS
 * app build, so the card shows a "manage on the web / in the store" note and
 * hides the in-app modify actions (Switch / Cancel / Manage / Resume). The web
 * (PayPro) rail is managed only on the web — a store app must not offer in-app
 * modify affordances for it (also keeps the store apps clear of anti-steering
 * rules).
 *
 * Computed client-side on purpose: it must NOT disturb the renews-vs-expires /
 * active-vs-canceled status display, which keys off `canCancelSubscription`
 * (server-derived). So the server flags are left alone and only the action
 * affordances are gated here.
 */
function isSubscribedOffPlatform(inputs: SubscriptionCardInputs): boolean {
  if (!inputs.provider) return false;
  // off-platform = the membership's provider can't be managed from here
  // (web/PayPro only on the web, each store only in its own app)
  return !isMembershipManageableOnPlatform(inputs.provider, inputs.platform);
}

// `canCancelHere` is `isInAppCancelAvailable(provider, platform)`, called inline
// above. It deliberately does NOT take the local-`licenseType` fallback that
// `canResumeHere` takes. That fallback exists to rescue a paused subscriber from
// an in-app dead end when `getSubscriptionInfo` fails transiently. Cancel has no
// equivalent dead end: the Cancel button is also gated on the server's
// `canCancelSubscription`, which is false on that same failure path, so adding
// the fallback there would change nothing except to imply the two are symmetric.
// A paused user on that path resumes first and can cancel after.

/**
 * Whether the in-app Resume mechanism is reachable here. Mirror of
 * `canCancelHere`: the web rails (PayPro and Paddle) resume via our server on any
 * platform; Apple resumes via the native StoreKit sheet on iOS only; Google Play
 * has no tested in-app resume path and is excluded.
 */
function isResumeReachableHere(inputs: SubscriptionCardInputs): boolean {
  // Fall back to the LOCAL licenseType when the server's provider is not known.
  // `provider` comes only from the getSubscriptionInfo response, so on a
  // transient failure it is null — and a paused subscriber would then get the
  // paused note, no Upgrade block (correctly suppressed) and NO Resume button:
  // the in-app dead end this feature exists to prevent. Same reasoning that makes
  // isPaddlePaused read local state. (#4189)
  //
  // Gated on the paused case rather than applied to every null provider, so the
  // code covers exactly what the paragraph above justifies. Without the gate it
  // also reached a past-due (Suspended) web subscriber on a failed load, which
  // was harmless but was never the point of the fallback.
  //
  // The literal is not a shortcut: isPausedPaddleSubscriber already requires
  // licenseType === Paddle, so a lookup here could only ever return 'paddle' and
  // would read as though other providers were covered.
  const provider = inputs.provider
    ?? (isPausedPaddleSubscriber(inputs.couch) ? 'paddle' : null);
  return isInAppResumeAvailable(provider, inputs.platform);
}

/**
 * The pending Paddle scheduled change, when the subscription has one — a cancel
 * the member requested, or a pause scheduled by Paddle Retain, that has not taken
 * effect yet. Selects WHICH note is shown ("cancels on" vs "pauses on"); the date
 * in that note is `licenseExpiry`, the same graced value the rest of the card
 * uses. Null when nothing is pending.
 *
 * Keys on the LOCAL licenseType, not `provider`, for the same reason
 * `isPaddlePaused` and `canResumeHere` do: `provider` is populated only from the
 * getSubscriptionInfo response, so a transient failure leaves it null. Reading it
 * there would drop this to null and tell a subscriber whose subscription is
 * scheduled to PAUSE that it "ends on <date>" — a wrong statement about their own
 * subscription, on the exact failure path the rest of this feature was hardened
 * against.
 *
 * `CouchUserPaddleScheduledChange.action` has three values, not two, and the
 * webhook mirrors all of them. That is why `hasPendingPaddleResume` exists
 * separately rather than the "ends on" notes being the else-branch of the pause
 * test: that branch also serves every NON-Paddle canceled subscription, so a
 * pending `resume` would fall into it and caption a returning subscriber with
 * "your subscription ends on <the resume date>" — the opposite of what is
 * happening. It should be unreachable in those blocks today (a scheduled resume
 * only exists while the subscription is still paused, and a paused subscriber is
 * not `roleClass === 'paid'`), but the server-side gates were hardened for
 * `resume` explicitly (see `computeCanCancelSubscription`), and the client should
 * not be the single place that assumes only two of the three actions can occur.
 */
function pendingPaddleChange(inputs: SubscriptionCardInputs): CouchUserPaddleScheduledChange | null {
  const isPaddle = inputs.provider === 'paddle'
    || inputs.couch?.licenseType === UserLicenseType.Paddle;
  if (!isPaddle) return null;
  return inputs.couch?.paddleData?.scheduledChange ?? null;
}

// `isPaddlePaused` is `isPausedPaddleSubscriber(couch)`, called inline above. It
// reads the LOCAL licenseType, not `provider`. `provider` is populated only from
// the getSubscriptionInfo response, so if that call fails it stays null — and then
// suppressUpgradeBlock would go false and a paused subscriber would be shown the
// free-user "Upgrade Now" block, i.e. invited to buy a second subscription. The
// local session data is always there. subscribe.ts's own paused gate reads
// licenseType for the same reason.

/**
 * Whether to show the Resume button. A paused subscription is ORed in because a
 * pause that has taken effect drops the paid role — without it a paused
 * subscriber would have no way back (the paid arm can never match once access
 * stopped).
 *
 * Suppressed while the web-membership problem note is on (#4247): in that state
 * the membership cannot be read at all, and `onResumeClick`'s fallback would push
 * a member who already pays us to the Subscribe page to buy a SECOND membership.
 * Kept here rather than in the pure policy because it is a card-level view
 * concern, not part of the entitlement rule.
 */
function isResumeButtonShown(
  inputs: SubscriptionCardInputs,
  derived: {
    hasPendingPaddlePause: boolean;
    isPaddlePaused: boolean;
    canResumeHere: boolean;
    showWebMembershipProblemNote: boolean;
  },
): boolean {
  if (derived.showWebMembershipProblemNote) return false;
  return shouldShowResumeButton({
    roleClass: inputs.roleClass,
    canCancelSubscription: inputs.canCancelSubscription,
    roleIsTemporary: inputs.roleIsTemporary,
    // the server's answer, not the local flag: a dunning subscription with a
    // pause scheduled is NOT resumable, and nothing local says so.
    hasResumablePendingPause: derived.hasPendingPaddlePause && inputs.serverCanResumeSubscription,
    isPaddlePaused: derived.isPaddlePaused,
    canResumeHere: derived.canResumeHere,
  });
}

/**
 * Whether to show the Manage Subscription button. It is offered only for a
 * web-rail subscription that is manageable from this platform: PayPro (sends a
 * login email) or Paddle (opens the customer portal). Hidden off-platform (a
 * store app must not offer web-rail management affordances).
 */
function isManageSubscriptionShown(inputs: SubscriptionCardInputs, subscribedOffPlatform: boolean): boolean {
  const isPayProManageable = inputs.hasPayProData && inputs.provider === 'payPro';
  const isPaddleManageable = inputs.hasPaddleData && inputs.provider === 'paddle';
  return (isPayProManageable || isPaddleManageable) && !subscribedOffPlatform;
}

/**
 * Show the "we can't manage this here" note. This covers a web-billed membership
 * (PayPro or Paddle) that the server reports as NOT manageable — in practice a
 * Paddle record whose stored subscription id is missing, so every action flag
 * comes back false.
 *
 * Without this note that member hits a silent dead end: no Cancel button, and no
 * "managed elsewhere" note either, because the off-platform note deliberately
 * stays quiet whenever an in-app cancel mechanism exists for the provider — which
 * it does for both web rails. So the panel simply rendered empty. Saying that
 * something is wrong, and where the membership is billed, is what issue #4247
 * asks for: never a dead end.
 */
function isWebMembershipProblemNoteShown(inputs: SubscriptionCardInputs): boolean {
  if (inputs.loadingSlowData) return false;
  if (inputs.roleClass !== 'paid') return false;
  return shouldShowWebMembershipProblemNote(inputs.provider, inputs.canManageSubscription);
}

/**
 * Show the "managed elsewhere" note only when the membership is off-platform AND
 * there is no in-app cancel/resume mechanism for it here. For a PayPro or Paddle
 * sub on the native app an in-app action always exists, so the old confusing note
 * disappears and the member sees Cancel (active) or Resume (canceled) instead.
 * Only store subs viewed on the wrong platform (a rare cross-login) still show
 * this note, since there is no in-app action there.
 *
 * The gap this leaves — a web-billed sub with NO usable action — is covered by the
 * web-membership problem note above, not here (#4247).
 */
function isOffPlatformNoteShown(
  inputs: SubscriptionCardInputs,
  derived: { subscribedOffPlatform: boolean; canCancelHere: boolean; canResumeHere: boolean },
): boolean {
  // subscribedOffPlatform is already false when provider is null, so a separate
  // provider null-check here would be dead — omit it.
  return !inputs.loadingSlowData && derived.subscribedOffPlatform
    && !derived.canCancelHere && !derived.canResumeHere;
}

/**
 * Show the note telling a coached client that their coach's plan already covers
 * the membership they are ALSO paying for themselves (#4462).
 *
 * The condition lives in `coach-covers-membership-policy.ts`; this only feeds it
 * the card's state and adds the two view-level gates the rule has no business
 * knowing about.
 *
 * `loadingSlowData` keeps the note in step with every other server-derived block
 * on this card, which all wait for the same flag.
 *
 * `subscriptionInfoError` is the one that changes behaviour, and it is here rather
 * than in the rule because it is a fact about THIS PAGE's last refresh, not about
 * the member. `provider` lives on a singleton service and is only ever assigned
 * after a successful `getSubscriptionInfo`, so a second visit whose refresh failed
 * still holds the first visit's answer. Without this gate the note would state a
 * money fact from data the page had just failed to reload — printed directly under
 * the card's own "Could not load subscription details" note.
 *
 * It covers a refresh that THREW, which is the only failure that flag records.
 * `synchronizeSlowData` has an earlier exit that it does not cover: when the
 * re-login returns false (offline, or a sign-out race) the method returns without
 * ever calling the server and without setting the flag, so a repeat visit in the
 * same session still renders the previous answer. That is left as it is on
 * purpose. The whole subscription card behaves that way offline, `couch` and the
 * subscription service go stale together so the two halves of the note stay
 * consistent with each other, and a cold offline boot has no `couch` at all, which
 * the rule already hides on.
 */
function isCoachCoversMembershipNoteShown(inputs: SubscriptionCardInputs): boolean {
  if (inputs.loadingSlowData || inputs.subscriptionInfoError) return false;
  return shouldShowCoachCoversMembershipNotice({
    coachData: inputs.couch?.coachData,
    ownSubscriptionProvider: inputs.provider,
    ownSubscriptionStatus: inputs.subscriptionStatus,
    now: inputs.now,
  });
}

/**
 * Which "current plan" note the regular-active block shows. Centralizes the
 * three-way branch (pending downgrade / upgrade-with-saving / plain current-plan)
 * so the template stays flat. Hidden when the block shouldn't render at all
 * (loading or no plans).
 */
function resolvePlanNoteMode(inputs: SubscriptionCardInputs): PlanNoteMode {
  if (inputs.loadingSlowData) return PlanNoteMode.Hidden;
  // while a renewal charge is failing there is no Switch button (canSwitchPlan
  // excludes that state), so the "you can switch to X and save Y" note would
  // invite an action the page refuses to provide — right next to the note saying
  // the last payment did not go through (#4247).
  if (inputs.isPaymentFailing) return PlanNoteMode.Hidden;
  if (inputs.pendingPlanChange) return PlanNoteMode.Pending;
  if (!inputs.currentPlan || !inputs.otherPlan) return PlanNoteMode.Hidden;
  // both remaining notes quote the other plan's price; Paddle plans carry no
  // priceText (see PaddleSubscriptionStrategy.loadPlans), so hide rather than show a blank price.
  if (!inputs.otherPlan.priceText) return PlanNoteMode.Hidden;
  if (inputs.otherPlan.saving) return PlanNoteMode.Saving;
  return PlanNoteMode.Plain;
}

/**
 * Which label the "Subscription type" row shows. Every plan today is monthly or
 * yearly (subscribe-native-rail.ts's recurrenceLabel makes the same 1/12
 * assumption). A plan of any other length falls back to the plan's own display
 * title rather than being confidently mislabeled "Yearly" — and is reported, via
 * `unknownRecurrenceMonths`.
 */
function resolveSubscriptionTypeLabelKind(currentPlan: SubscriptionCardPlan | null): SubscriptionTypeLabelKind {
  if (!currentPlan) return SubscriptionTypeLabelKind.None;
  if (currentPlan.numMonths === 1) return SubscriptionTypeLabelKind.Monthly;
  if (currentPlan.numMonths === 12) return SubscriptionTypeLabelKind.Yearly;
  return SubscriptionTypeLabelKind.PlanTitle;
}

/**
 * The plan length to report as an anomaly, or null when there is nothing to report.
 *
 * Only reported where the card actually SHOWS the type label. The label used to be
 * a getter that logged as a side effect of being read, and only two blocks of the
 * card read it — so a member the card never shows the label to never produced a
 * report, and this keeps that true. Without the gate, a free or paid-temporary
 * member holding an odd-length plan would start reporting.
 */
function resolveUnknownRecurrenceMonths(inputs: SubscriptionCardInputs): number | null {
  if (!isSubscriptionTypeLabelShown(inputs)) return null;
  if (resolveSubscriptionTypeLabelKind(inputs.currentPlan) !== SubscriptionTypeLabelKind.PlanTitle) return null;
  return inputs.currentPlan!.numMonths;
}

/**
 * Whether any block of the card shows the "Subscription type" label. These are the
 * two template blocks that read it, and must stay in step with them:
 * - the paid-member details grid, for a paid, non-temporary member with an expiry
 *   or a known plan;
 * - the details block for a role that is neither free nor paid (VIP, developer...).
 */
function isSubscriptionTypeLabelShown(inputs: SubscriptionCardInputs): boolean {
  const isPaidDetailsGridShown = inputs.roleClass === 'paid' && !inputs.roleIsTemporary
    && (!!inputs.licenseExpiry || !!inputs.currentPlan);
  const isOtherRoleDetailsShown = inputs.roleClass !== 'unknown' && inputs.roleClass !== 'paid';
  return isPaidDetailsGridShown || isOtherRoleDetailsShown;
}
